import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import VehicleSelector from './VehicleSelector'
import DutyTimes from './DutyTimes'
import CrewShifts from './CrewShifts'
import AddExistingShift from './AddExistingShift'
import AssignedShifts from './AssignedShifts'
import AddShiftModal from '../AddShiftModel'
import { PTSDutiesContext } from '..'
import Button from '@mui/material/Button'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import BaseLocationSelector from './BaseLocationSelector'
import API from '../../../../services/API'
import SmallModal from '../../../../components/Modals/SmallModal'
import RepeatDutyPanel from './RepeatDutyPanel'
import {
  buildCrewTemplates,
  buildOccurrences,
  buildRepeatPayload,
  defaultRepeat,
  findDuplicateSeats,
  isObjectId,
  MAX_REPEAT_DUTIES,
  MAX_REPEAT_SHIFTS,
} from './dutyRepeat'

// Server issues from /pts/duties/repeat, grouped by duty
const groupIssuesByOccurrence = (issues = []) =>
  issues.reduce((grouped, issue) => {
    if (!Number.isInteger(issue?.occurrence)) return grouped
    grouped[issue.occurrence] = [...(grouped[issue.occurrence] ?? []), issue]
    return grouped
  }, {})

const getDutySaveErrorMessage = (err) => {
  const data = err?.response?.data || {}
  if (Array.isArray(data.issues) && data.issues.length) {
    return data.issues.map((issue) => issue.message).join(' ')
  }
  return data.error || data.message || 'Failed to save duty.'
}

const CreateDutyForm = ({
  afterEffect,
  editDuty,
  selectedDate,
  newFromBooking,
  onClose,
  onRepeatChange,
}) => {
  const {
    getUnassignedShifts,
    shiftsWithoutDuty,
    setShiftsWithoutDuty,
    reloadDuty,
  } = useContext(PTSDutiesContext)

  const { baseLocationOptions } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig || {}
  const currency = orgSettings?.currency ?? '£'

  const [showHelperText, setShowHelperText] = useState(false)
  const [assignShift, setAssignShift] = useState(false)
  const [error, setError] = useState('')

  const [openNewShift, setOpenNewShift] = useState(false)
  const [openAddExistingShift, setOpenAddExistingShift] = useState(false)

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)

  const [loading, setLoading] = useState(false)

  const editDutyOriginalForm = {
    vehicle: editDuty?.vehicle || null,
    baseLocation: editDuty?.baseLocation
      ? baseLocationOptions.find((x) => x._id === editDuty.baseLocation)
      : null,
    start: moment(editDuty?.start),
    end: moment(editDuty?.end),
    crewShifts: editDuty?.crewShifts || [],
    notes: editDuty?.notes || '',
    organisation: editDuty?.organisation || null,
    crewSelfDispatch: !!editDuty?.crewSelfDispatch,
    billing: {
      mode: editDuty?.billing?.mode || 'perJourney',
      basis: editDuty?.billing?.basis || 'fixed',
      rate: editDuty?.billing?.rate ?? '',
      notes: editDuty?.billing?.notes || '',
    },
  }

  const [form, setForm] = useState({
    vehicle: null,
    baseLocation:
      (baseLocationOptions ?? []).find((b) =>
        user.selectedBaseLocations.includes(b._id),
      ) ?? null,
    start: moment(selectedDate).hour(7).startOf('hour'),
    end: moment(selectedDate).hour(19).startOf('hour'),
    crewShifts: [],
    shiftsToDelete: [],
    notes: '',
    organisation: null,
    crewSelfDispatch: false,
    billing: { mode: 'perJourney', basis: 'fixed', rate: '', notes: '' },
  })

  // Dedicated duty: reserved for one hospital's journeys only
  const [dedicated, setDedicated] = useState(!!editDuty?.organisation)
  const [dedicatedConfirmed, setDedicatedConfirmed] = useState(
    !!editDuty?.organisation,
  )
  const [orgOptions, setOrgOptions] = useState([])

  useEffect(() => {
    if (!dedicated || orgOptions.length) return
    API.get('/pts/organisationoptions?type=PTS')
      .then((res) => {
        if (res.status === 200) setOrgOptions(res.data || [])
      })
      .catch((e) => console.error(e))
  }, [dedicated])

  const selectedOrganisation =
    orgOptions.find(
      (org) =>
        String(org._id) ===
        String(form.organisation?._id || form.organisation || ''),
    ) ||
    (typeof form.organisation === 'object' ? form.organisation : null)

  // Repeating only applies to a brand-new duty made from the planning page; a
  // duty made from a booking is for that one journey
  const canRepeat = !editDuty && !newFromBooking
  const [repeat, setRepeat] = useState(() => defaultRepeat(form.start))
  const [repeatIssues, setRepeatIssues] = useState({})
  const repeating = canRepeat && !!repeat.enabled

  const occurrences = useMemo(
    () => (repeating ? buildOccurrences(form, repeat) : []),
    [repeating, form.start, form.end, repeat],
  )
  const crewTemplates = useMemo(
    () => buildCrewTemplates(form.crewShifts),
    [form.crewShifts],
  )
  const duplicateSeats = useMemo(
    () =>
      repeating ? findDuplicateSeats(repeat, occurrences, crewTemplates) : {},
    [repeating, repeat, occurrences, crewTemplates],
  )
  const repeatShiftCount = occurrences.length * crewTemplates.length
  const repeatBlocked =
    repeating &&
    (!occurrences.length ||
      occurrences.length > MAX_REPEAT_DUTIES ||
      repeatShiftCount > MAX_REPEAT_SHIFTS ||
      Object.keys(duplicateSeats).length > 0)

  useEffect(() => {
    onRepeatChange?.(repeating)
  }, [repeating])

  // Server issues describe the batch as it was sent - drop them once it changes
  useEffect(() => {
    setRepeatIssues({})
  }, [repeat, form.start, form.end, form.crewShifts, form.vehicle])

  const toggleRepeat = (enabled) =>
    setRepeat((prev) => {
      if (!enabled) return { ...prev, enabled: false }
      const start = moment(form.start)
      const fresh = defaultRepeat(start)
      return {
        ...prev,
        enabled: true,
        daysOfWeek: prev.daysOfWeek?.length
          ? prev.daysOfWeek
          : fresh.daysOfWeek,
        until:
          prev.until && !moment(prev.until).isBefore(start, 'day')
            ? prev.until
            : fresh.until,
      }
    })

  const wrongTimes =
    !editDuty &&
    selectedDate &&
    (moment(selectedDate).isBefore(form.start) ||
      moment(selectedDate).isAfter(form.end))

  useEffect(() => {
    if (editDuty) {
      setForm(editDutyOriginalForm)
    }
  }, [editDuty])

  //   useEffect(() => {
  //     API.get(
  //       `/pts/shiftswithoutduty?startDate=${moment(form?.start).toISOString()}&endDate=${moment(form?.end).toISOString()}`,
  //     ).then((res) => {
  //       if (res.status === 200) {
  //         setShiftsWithoutDuty(res.data)
  //         console.log('uncovered', res.data)
  //       }
  //     })
  //   }, [])

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleDelete = () => {
    API.delete(`/pts/duties/${editDuty._id}`).then((res) => {
      if (res.status === 200) {
        onClose()
        reloadDuty()
      } else {
        setError(res.data?.error || res.data?.message || 'Failed to delete duty.')
        setOpenConfirmDelete(false)
      }
    })
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError('')

    if (dedicated && !selectedOrganisation?._id) {
      setError('Select the organisation this duty is dedicated to.')
      setLoading(false)
      return
    }
    if (dedicated && !dedicatedConfirmed) {
      setError(
        'Please confirm you understand this duty will only be available to the selected organisation.',
      )
      setLoading(false)
      return
    }
    if (
      dedicated &&
      form.billing?.mode === 'wholeDuty' &&
      !(Number(form.billing?.rate) > 0)
    ) {
      setError('Enter a rate for whole-duty billing.')
      setLoading(false)
      return
    }

    // Shared by a single duty and every duty in a repeat
    const dutySettings = {
      organisation: dedicated ? selectedOrganisation?._id || null : null,
      // Crew self-dispatch only exists on a dedicated duty
      crewSelfDispatch: dedicated && !!form.crewSelfDispatch,
      // Whole-duty billing only makes sense on a dedicated duty — reset it
      // if the dedication is removed
      billing:
        dedicated && form.billing?.mode === 'wholeDuty'
          ? {
              mode: 'wholeDuty',
              basis: form.billing?.basis === 'hourly' ? 'hourly' : 'fixed',
              rate: Number(form.billing?.rate),
              notes: form.billing?.notes || '',
            }
          : { mode: 'perJourney' },
    }

    if (repeating) {
      try {
        if (repeatBlocked) {
          setError(
            Object.keys(duplicateSeats).length
              ? 'Someone is in two seats on the same duty — check the duties marked “Needs attention”.'
              : 'Adjust the repeat pattern before creating the duties.',
          )
          return
        }
        const res = await API.post(
          '/pts/duties/repeat',
          buildRepeatPayload({
            dutyFields: {
              ...dutySettings,
              vehicle: form.vehicle?._id ?? null,
              baseLocation: form.baseLocation?._id ?? form.baseLocation ?? null,
              notes: form.notes,
            },
            form,
            repeat,
            occurrences,
            templates: crewTemplates,
          }),
        )
        if (res.status < 200 || res.status >= 300) {
          setRepeatIssues(groupIssuesByOccurrence(res.data?.issues))
          setError(
            res.data?.errorCode === 'dutyRepeatBlocked'
              ? res.data.error
              : getDutySaveErrorMessage({ response: res }),
          )
          return
        }
        getUnassignedShifts?.()
        onClose && onClose()
      } catch (err) {
        console.error(err)
        setError(getDutySaveErrorMessage(err))
      } finally {
        setLoading(false)
      }
      return
    }

    try {
      if (form.vehicle) {
        const overlapRes = await API.get(
          `/pts/duties/check-overlap?vehicle=${
            form.vehicle._id
          }&start=${form.start.toISOString()}&end=${form.end.toISOString()}${
            editDuty ? `&exclude=${editDuty._id}` : ''
          }`,
        )
        if (overlapRes.data.overlap) {
          setError(
            overlapRes.data.vehicleConflict?.message ||
              'This vehicle is already unavailable during this time.',
          )
          setLoading(false)
          return
        }
      }

      const shiftsToDelete = (editDuty?.crewShifts || [])
        .filter((shift) => !form.crewShifts.some((x) => x._id === shift._id))
        .map((shift) => shift._id)

      const payload = {
        ...form,
        start: form.start.toISOString(),
        end: form.end.toISOString(),
        crewShifts: (form.crewShifts ?? [])
          .map((shift) => shift._id)
          .filter(isObjectId),
        crewShiftDrafts: (form.crewShifts ?? []).filter(
          (shift) => !isObjectId(shift._id),
        ),
        shiftsToDelete,
        vehicle: form?.vehicle?._id ?? null,
        ...dutySettings,
      }

      const res = editDuty
        ? await API.put(`/pts/duties/${editDuty._id}`, payload)
        : await API.post('/pts/duties', payload)

      if (res.status < 200 || res.status >= 300) {
        setError(getDutySaveErrorMessage({ response: res }))
        return
      }

      if (typeof getUnassignedShifts === 'function') {
        getUnassignedShifts()
      }
      afterEffect &&
        afterEffect({
          ...res.data,
          vehicle: form?.vehicle ?? res.data?.vehicle ?? null,
        })
      // onSave()
      onClose && onClose()
    } catch (err) {
      console.error(err)
      setError(getDutySaveErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={baseLocationOptions?.length > 1 ? 6 : 12}>
        <VehicleSelector form={form} handleChange={handleChange} />
      </Grid>
      {baseLocationOptions?.length > 1 && (
        <Grid item xs={12} lg={6}>
          <BaseLocationSelector form={form} handleChange={handleChange} />
        </Grid>
      )}

      {/* Times */}
      <DutyTimes form={form} handleChange={handleChange} />
      {selectedDate && wrongTimes && newFromBooking && (
        <Grid item xs={12}>
          <Alert severity="error" variant="outlined">
            These times do not cover the patient pickup/dropoff time
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* Dedicated hospital duty */}
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dedicated}
              onChange={(event) => {
                setDedicated(event.target.checked)
                if (!event.target.checked) {
                  setDedicatedConfirmed(false)
                  handleChange('organisation', null)
                  handleChange('crewSelfDispatch', false)
                }
              }}
            />
          }
          label="Dedicate this duty to a hospital / organisation (dedicated crew)"
        />
        {dedicated && (
          <Stack spacing={1.5} mt={1} width='100%'>
            <Autocomplete
              options={orgOptions}
              value={selectedOrganisation}
              onChange={(event, org) => handleChange('organisation', org)}
              getOptionLabel={(option) => option?.orgName || ''}
              isOptionEqualToValue={(option, value) =>
                String(option?._id) === String(value?._id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dedicated to organisation"
                  required
                />
              )}
            />
            <Alert severity="warning">
              <b>
                This duty, its vehicle and its crew will be available to{' '}
                {selectedOrganisation?.orgName || 'the selected organisation'}{' '}
                only.
              </b>{' '}
              It will not appear as an option for general PTS planning or other
              customer's journeys. The hospital will be able to book journeys
              directly onto this crew through their portal — those bookings are
              approved automatically.
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.crewSelfDispatch}
                  onChange={(event) =>
                    handleChange('crewSelfDispatch', event.target.checked)
                  }
                />
              }
              label="No dispatch — the crew add their own jobs"
            />
            {form.crewSelfDispatch && (
              <Alert severity="info">
                Use this when{' '}
                {selectedOrganisation?.orgName || 'the hospital'} tells the crew
                about discharges and transfers directly. Once clocked in, the
                crew can add a job from My PTS Duty and fill in the patient
                details and destination as they get them — a job can&apos;t be
                completed until they have. Each job is a normal booking for{' '}
                {selectedOrganisation?.orgName || 'this organisation'}
                {form.billing?.mode === 'wholeDuty'
                  ? '.'
                  : ', flagged for a co-ordinator to review before it is invoiced.'}
              </Alert>
            )}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                select
                label="Billing"
                size="small"
                sx={{ minWidth: 220 }}
                value={form.billing?.mode || 'perJourney'}
                onChange={(e) =>
                  handleChange('billing', {
                    ...form.billing,
                    mode: e.target.value,
                  })
                }
                fullWidth
              >
                <MenuItem value="perJourney">
                  Per journey (tariff pricing)
                </MenuItem>
                <MenuItem value="wholeDuty">Whole duty</MenuItem>
              </TextField>
              {form.billing?.mode === 'wholeDuty' && (
                <>
                  <TextField
                    select
                    label="Basis"
                    size="small"
                    sx={{ minWidth: 160 }}
                    value={form.billing?.basis || 'fixed'}
                    onChange={(e) =>
                      handleChange('billing', {
                        ...form.billing,
                        basis: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="fixed">Fixed price</MenuItem>
                    <MenuItem value="hourly">Hourly rate</MenuItem>
                  </TextField>
                  <TextField
                    label={
                      form.billing?.basis === 'hourly'
                        ? 'Rate per hour'
                        : 'Duty price'
                    }
                    size="small"
                    type="number"
                    sx={{ minWidth: 150 }}
                    value={form.billing?.rate ?? ''}
                    onChange={(e) =>
                      handleChange('billing', {
                        ...form.billing,
                        rate: e.target.value,
                      })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {currency}
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </Stack>
            {form.billing?.mode === 'wholeDuty' && (
              <Alert severity="info">
                {selectedOrganisation?.orgName || 'The organisation'} will be
                invoiced for this duty as a whole
                {form.billing?.basis === 'hourly'
                  ? ` (duty hours × ${currency}${form.billing?.rate || '…'}/h)`
                  : ` (${currency}${form.billing?.rate || '…'} fixed)`}{' '}
                by the next billing run after the duty ends. Journeys carried on
                this duty are <b>not</b> charged individually.
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={dedicatedConfirmed}
                  onChange={(event) =>
                    setDedicatedConfirmed(event.target.checked)
                  }
                />
              }
              label="I understand — reserve this duty for this organisation's journeys"
            />
          </Stack>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <CrewShifts
        form={form}
        setForm={setForm}
        assignShift={assignShift}
        setAssignShift={setAssignShift}
        setOpenNewShift={setOpenNewShift}
        setOpenAddExistingShift={setOpenAddExistingShift}
        hasShiftsWithoutDuty={shiftsWithoutDuty?.length > 0 ? true : false}
      />

      <AddExistingShift
        // shiftsWithoutDuty={shiftsWithoutDuty}
        open={openAddExistingShift}
        onClose={() => setOpenAddExistingShift(false)}
        form={form}
        handleChange={handleChange}
      />
      <AssignedShifts
        form={form}
        setForm={setForm}
        setShiftsWithoutDuty={setShiftsWithoutDuty}
        setAssignShift={setAssignShift}
      />

      <AddShiftModal
        open={!!assignShift}
        onClose={() => setAssignShift(null)}
        existingShift={assignShift}
        duty={form}
        setDuty={setForm}
      />

      {/* AddShiftModal */}
      <AddShiftModal
        open={openNewShift}
        onClose={() => {
          setOpenNewShift(false)
        }}
        duty={editDuty || { start: form.start, end: form.end }}
        onCreate={(newShift) => {
          // setShifts((prev) => [...prev, newShift])
          setForm((prev) => ({
            ...prev,
            crewShifts: [...prev.crewShifts, newShift],
          }))
        }}
      />

      {canRepeat && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              sx={{ mx: 0, alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={repeating}
                  onChange={(e) => toggleRepeat(e.target.checked)}
                />
              }
              label={
                <Box ml={0.5} mt={0.75}>
                  <Typography fontWeight={600}>Repeat this duty</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create the same duty on other days — e.g. every weekday for
                    a month — and crew each one.
                  </Typography>
                </Box>
              }
            />
            {repeating && (
              <Box mt={1.5}>
                <RepeatDutyPanel
                  form={form}
                  repeat={repeat}
                  setRepeat={setRepeat}
                  occurrences={occurrences}
                  templates={crewTemplates}
                  issuesByOccurrence={repeatIssues}
                  duplicateSeats={duplicateSeats}
                />
              </Box>
            )}
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* Notes */}
      <Grid item xs={12}>
        <TextField
          label="Notes"
          multiline
          fullWidth
          rows={2}
          value={form.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} lg={4}>
        {editDuty && !form?.crewShifts?.length && (
          <Button
            onClick={() => setOpenConfirmDelete(true)}
            variant="outlined"
            color="anRed"
          >
            Delete
          </Button>
        )}
      </Grid>
      <Grid item xs={12} lg={8}>
        <Stack direction="row" gap={1} justifyContent="flex-end">
          {onClose && <Button onClick={onClose}>Cancel</Button>}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || repeatBlocked}
          >
            {editDuty
              ? 'Save Changes'
              : repeating
                ? loading
                  ? 'Creating duties…'
                  : `Create ${occurrences.length} ${
                      occurrences.length === 1 ? 'Duty' : 'Duties'
                    }`
                : 'Create Duty'}
          </Button>
        </Stack>
      </Grid>
      <SmallModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
      >
        <Stack gap={1} alignItems="center">
          <Typography textAlign="center">
            Are you sure you want to delete this PTS Duty?
          </Typography>
          <Typography variant="caption">This cannot be undone</Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => onClose()}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            fullWidth
          >
            Delete
          </Button>
        </Stack>
      </SmallModal>
    </Grid>
  )
}

export default CreateDutyForm
